import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Custom hook that provides a function to switch languages while 
 * maintaining the same route path.
 * 
 * @returns A function to switch languages that takes a language code
 */
const useLanguageSwitch = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Switches the application language and updates the URL path.
   * 
   * @param lang The language code to switch to ('en', 'fr', 'ar')
   */
  const switchLanguage = useCallback((lang: string) => {
    const currentLanguage = i18n.language.split('-')[0];
    
    if (lang === currentLanguage) return;

    // Get current path
    const pathname = location.pathname;
    
    // Extract the path without the language prefix
    const pathParts = pathname.split('/').filter(Boolean);
    const currentPath = pathParts.length > 1 ? pathParts.slice(1).join('/') : '';
    
    // Construct new URL with new language prefix
    const newPath = `/${lang}/${currentPath}`;
    
    // Change language in i18n
    i18n.changeLanguage(lang).then(() => {
      // Navigate to the new URL
      navigate(newPath);
    });
  }, [i18n, navigate, location.pathname]);

  return switchLanguage;
};

export default useLanguageSwitch; 